<script setup>
import { ref, onMounted, watch } from 'vue'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import AppLoader from '@/components/AppLoader.vue'
// Importujemy Vue3 Easy Data Table i jego CSS
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { webAlert, showConfirm } from '@/utils/dialog'

// --- 1. Konfiguracja Easy Data Table ---
const headers = [
    { text: 'ID', value: 'ID', sortable: true, width: 70 },
    { text: 'Nazwa', value: 'name', sortable: true },
    { text: '', value: 'actions', width: 100 }
]

const items = ref([])
const loading = ref(false)
const saving = ref(false)
const savingTXT = ref('')
const searchValue = ref('')

const serverItemsLength = ref(0)
const serverOptions = ref({
    page: 1,
    rowsPerPage: 10,
    sortBy: 'ID',
    sortType: 'desc'
})

// --- 2. Pobieranie danych z API ---
const loadData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                method: 'post',
                action: 'get-drivetypes',
                page: serverOptions.value.page,
                limit: serverOptions.value.rowsPerPage,
                sortBy: serverOptions.value.sortBy,
                sortType: serverOptions.value.sortType,
                search: searchValue.value

            }
        )

        items.value = Array.isArray(response.data?.data) ? response.data.data : []
        serverItemsLength.value = response.data?.total || 0
    } catch (error) {
        webAlert('Błąd podczas ładowania danych z serwera.')
    } finally {
        loading.value = false
    }
}

// Rewatch na zmiany stron, limitów i sortowania
watch(
    serverOptions,
    () => {
        loadData()
    },
    { deep: true }
)

let searchTimeout = null
const handleSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        serverOptions.value.page = 1
        loadData()
    }, 400) // Debounce, żeby nie strzelać do API przy każdym naciśnięciu klawisza
}

// --- 3. Obsługa Modala i Formularza ---
const modalRef = ref(null)
const formRef = ref(null)
let modalInstance = null

const formTitle = ref('Dodaj typ napędu')
const formData = ref({
    ID: '',
    name: ''
})

const openAddModal = () => {
    formTitle.value = 'Dodaj nową wartość'
    formData.value = { ID: '', name: '' }
    modalInstance.show()
}

const openEditModal = async (id) => {
    savingTXT.value = 'Pobieranie danych ...';
    saving.value = true; // Włączamy nasze czekadełko (AppLoader)
    try {
        const response = await apiClient.post({
                action: 'get-drivetypes',
                ID: id
        })

        const result = await response.data
        const item = result.data[0]

        formTitle.value = `Edytuj: ${item.name}`
        formData.value = { ...item }
        modalInstance.show()
    } catch (error) {
        saving.value = false; 
        webAlert('Błąd podczas pobierania danych elementu.')
    } finally {
        saving.value = false // Wyłączamy czekadełko niezależnie od wyniku
    }
}

// Zapis danych (FormData -> x-www-form-urlencoded)
const handleSubmit = async () => {
    savingTXT.value = 'Zapisywanie ...';
    saving.value = true // Włączamy nasze czekadełko (AppLoader)
    try {
        const response = await apiClient.post(
            {
                action: 'save-drivetype',
                id: formData.value.ID || null,
                name: formData.value.name
            }
        )

        modalInstance.hide()
        saving.value = false;
        await loadData() // Odświeżenie tabeli po zapisie

        webAlert(response.data)
    } catch (error) {
        saving.value = false;
        webAlert('Błąd podczas zapisywania danych.')
    }
}

// Usuwanie elementu
const deleteItem = async (id) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć ten typ napędu?', {
        title: 'Usuwanie pozycji',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })

    if (!confirmed) return

    saving.value = true
    savingTXT.value = "Kasowanie ..."
    try {
        const response = await apiClient.post(
            {
                action: 'delete-drivetype',
                id: id
            }
        )

        saving.value = false; 
        await loadData() // Odświeżenie tabeli po usunięciu

        webAlert(response.data)
    } catch (error) {
        saving.value = false;
        webAlert('Błąd podczas usuwania pozycji.')
    } finally {
        saving.value = false;
    }
}

// Cykl życia
onMounted(() => {
    modalInstance = new Modal(modalRef.value)

    modalRef.value.addEventListener('shown.bs.modal', () => {
        const firstInput = modalRef.value.querySelector('input:not([type="hidden"]), select, textarea')
        if (firstInput) {
            firstInput.focus()
            // opcjonalnie: firstInput.select() -> jeśli chcesz od razu zaznaczyć tekst w polu do edycji
        }
    })

    loadData()
})
</script>

<template>
    <div class="container-fluid">

        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Typy napędu</h3>
                <p class="text-muted small mb-0">Zarządzanie typami napędu w systemie</p>
            </div>
            <button
                class="btn btn-primary-dark d-flex align-items-center justify-content-center gap-2 w-100 btn-responsive-auto"
                @click="openAddModal"> <i class="bi bi-plus-lg"></i>
                <span>Dodaj nowy</span>
            </button>
        </div>

        <!-- Tabela z Paskiem Wyszukiwania -->
        <div class="card border-0 shadow-sm">
            <div class="card-body">
                <!-- Wyszukiwarka -->
                <div class="row">
                    <div class="col-12 col-md-8"></div>
                    <div class="col-12 col-md-4">
                        <input v-model="searchValue" @input="handleSearch" type="text" class="form-control mb-3"
                            placeholder="Szukaj..." />
                    </div>
                </div>


                <!-- Komponent Vue3 Easy Data Table -->
                <div class="table-responsive">
                    <Vue3EasyDataTable v-model:server-options="serverOptions" :headers="headers" :items="items"
                        :loading="loading" :server-items-length="serverItemsLength" buttons-pagination
                        class="table-font-13" table-class-name="table table-hover align-middle custom-easy-table"
                        :rows-items="[10, 25, 50, 100]" empty-message="Brak danych do wyświetlenia"
                        rows-per-page-message="Wierszy na stronę:">
                        <!-- Customowa kolumna Kod -->
                        <template #loading>
                            <div class="py-4 text-center">
                                Pobieranie danych, proszę czekać...
                            </div>
                        </template>

                        <template #item-code="item">
                            <span class="badge bg-light text-dark border">{{ item.code }}</span>
                        </template>

                        <!-- Customowa kolumna Akcje -->
                        <template #item-actions="item">
                            <div class="d-flex justify-content-end gap-1">
                                <button class="btn btn-sm btn-primary-light" title="Edytuj"
                                    @click="openEditModal(item.ID)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" title="Usuń" @click="deleteItem(item.ID)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- Modal Formularza (Bez zmian) -->
        <div ref="modalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">{{ formTitle }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <form ref="formRef" @submit.prevent="handleSubmit">
                        <div class="modal-body">
                            <input type="hidden" name="id" :value="formData.id" />

                            <div class="mb-3">
                                <label class="form-label">Nazwa</label>
                                <input type="text" class="form-control" name="name" v-model="formData.name" required />
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary">Zapisz zmiany</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    <div id="glownyloader">
        <AppLoader :show="saving" :text="savingTXT" />
    </div>
</template>

<style scoped>
/* Dopasowanie nagłówków i stopki tabeli do stylu Bootstrap */
.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    max-width: calc(100vw - 2rem) !important;
    -webkit-overflow-scrolling: touch;
}

:deep(.vue3-easy-data-table) {
    --easy-table-border: 1px solid #dee2e6;
    --easy-table-row-border: 1px solid #dee2e6;
    --easy-table-header-font-size: 0.85rem;
    --easy-table-header-height: 45px;
    --easy-table-body-row-height: 50px;
    width: 100% !important;
    max-width: 100% !important;
}

:deep(.vue3-easy-data-table__main) {
    width: 100% !important;
    overflow-x: auto !important;
}

.buttons-pagination .item.button.active {
    background-color: #043873 !important;
}
</style>